import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ShipperNetwork } from "@/hooks/useShipperNetworks";
import { BROKER_GROUP_COLORS, DEFAULT_GROUP_COLOR_HEX, getBrokerGroupColor } from "@/lib/lrDirect/brokerGroupColors";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  network?: ShipperNetwork | null;
  onSubmit: (values: { name: string; description: string | null; is_default: boolean; color: string }) => Promise<void>;
  hasAnyNetworks: boolean;
}

export function NetworkFormDialog({ open, onOpenChange, network, onSubmit, hasAnyNetworks }: Props) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [color, setColor] = useState<string>(DEFAULT_GROUP_COLOR_HEX);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(network);

  useEffect(() => {
    if (open) {
      setName(network?.name ?? "");
      setDescription(network?.description ?? "");
      setIsDefault(network?.is_default ?? !hasAnyNetworks);
      // Existing groups: resolve stored value (preset key or hex) to a hex.
      // New groups: default to WHITE (no tint) until user picks.
      setColor(network?.color ? getBrokerGroupColor(network.color).hex : DEFAULT_GROUP_COLOR_HEX);
    }
  }, [open, network, hasAnyNetworks]);

  const handleSave = async () => {
    const n = name.trim();
    if (!n) { toast.error("Name is required"); return; }
    if (n.length > 80) { toast.error("Name must be 80 characters or fewer"); return; }
    setSaving(true);
    try {
      await onSubmit({ name: n, description: description.trim() || null, is_default: isDefault, color });
      toast.success(isEdit ? "Group updated" : "Group created");
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message || "Failed to save group");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Group" : "Create Group"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="net-name">Name</Label>
            <Input
              id="net-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              placeholder="e.g. West Coast Brokers"
              disabled={saving}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="net-desc">Description <span className="text-muted-foreground text-xs">(optional)</span></Label>
            <Textarea
              id="net-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={300}
              rows={3}
              placeholder="What's this group for?"
              disabled={saving}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="net-color">Color</Label>
            <div className="flex items-center gap-3">
              <label
                htmlFor="net-color"
                className="relative h-10 w-10 rounded-full border-2 border-border cursor-pointer overflow-hidden shrink-0"
                style={{ backgroundColor: color }}
                title="Pick any color"
              >
                <input
                  id="net-color"
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value.toUpperCase())}
                  disabled={saving}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
              </label>
              <Input
                value={color}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  if (/^#?[0-9a-fA-F]{0,6}$/.test(v)) {
                    setColor(v.startsWith("#") ? v.toUpperCase() : `#${v.toUpperCase()}`);
                  }
                }}
                maxLength={7}
                className="h-9 w-28 font-mono text-xs uppercase"
                disabled={saving}
              />
              <div className="flex flex-wrap gap-1.5">
                {BROKER_GROUP_COLORS.map((c) => {
                  const isSel = color.toUpperCase() === c.hex.toUpperCase();
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setColor(c.hex)}
                      disabled={saving}
                      title={c.label}
                      aria-label={c.label}
                      className={cn(
                        "relative h-6 w-6 rounded-full border transition-all",
                        isSel ? "ring-2 ring-offset-1 ring-brand-teal scale-110" : "hover:scale-105 border-border"
                      )}
                      style={{ backgroundColor: c.hex }}
                    >
                      {isSel && (
                        <Check
                          className="absolute inset-0 m-auto h-3 w-3"
                          style={{ color: c.hex === "#FFFFFF" || c.key === "lime" || c.key === "yellow" ? "#000" : "#fff" }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">Pick any color, or use a preset. Defaults to white.</p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <Checkbox
              checked={isDefault}
              disabled={saving || (isEdit && network?.is_default)}
              onCheckedChange={(v) => setIsDefault(Boolean(v))}
            />
            <span className="text-sm">
              Default group
              <span className="text-muted-foreground text-xs ml-1">(used when posting private loads)</span>
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            style={{ backgroundColor: "hsl(var(--brand-teal))" }}
            className="text-white"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
